import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Input } from "./ui/input";
import {
  Loader2, Search, Sparkles, CheckCircle2, AlertCircle,
  TrendingUp, Users, Brain, ArrowRight, Merge,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";

const PGC_LABELS = {
  "600": "Compres mercaderies",
  "601": "Compres matèries primeres",
  "602": "Compres altres aprovisionaments",
  "621": "Arrendaments i cànons",
  "622": "Reparacions i conservació",
  "623": "Serveis professionals",
  "624": "Transports",
  "625": "Primes d'assegurances",
  "626": "Serveis bancaris",
  "627": "Publicitat i RRPP",
  "628": "Subministraments",
  "629": "Altres serveis",
  "631": "Altres tributs",
};

/**
 * Per-supplier learning dashboard (FASE 6).
 *
 * Consumes GET /received-invoices/supplier-stats and shows:
 *  • KPI strip: total suppliers, auto-learned count, total invoices.
 *  • Filter bar: search by name/NIF + "Only auto-learned" toggle.
 *  • Detailed table with the dominant PGC account per supplier.
 */
export default function SuppliersDashboard({ onDrillDown }) {
  const { lang } = useI18n();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [onlyLearned, setOnlyLearned] = useState(false);
  const [deduping, setDeduping] = useState(false);

  const loadRows = async () => {
    try {
      const r = await api.get("/received-invoices/supplier-stats",
                              { params: { limit: 200 } });
      setRows(r.data.suppliers || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/received-invoices/supplier-stats",
                                { params: { limit: 200 } });
        if (alive) setRows(r.data.suppliers || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Error");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Detect supplier name variants per NIF (same NIF, different names).
  // Shows the "Fusionar variants" button only when there's something to fix.
  const variantGroups = useMemo(() => {
    const byNif = {};
    for (const r of rows) {
      const nif = (r.supplier_nif || "").trim();
      if (!nif) continue;
      byNif[nif] = byNif[nif] || new Set();
      byNif[nif].add(r.supplier_name || "");
    }
    return Object.entries(byNif).filter(([, names]) => names.size > 1);
  }, [rows]);

  const dedupe = async () => {
    if (!window.confirm(
      lang === "es"
        ? "Esto unificará las variantes del nombre de un mismo proveedor (mismo NIF) usando el más utilizado. ¿Continuar?"
        : "Això unificarà les variants del nom d'un mateix proveïdor (mateix NIF) usant la més utilitzada. Continuar?"
    )) return;
    setDeduping(true);
    try {
      const r = await api.post("/received-invoices/dedupe-suppliers");
      const n = r.data.invoices_updated || 0;
      toast.success(
        lang === "es"
          ? `${n} factura${n === 1 ? "" : "s"} actualizada${n === 1 ? "" : "s"} en ${r.data.groups_merged} grupo${r.data.groups_merged === 1 ? "" : "s"}`
          : `${n} factura${n === 1 ? "" : "es"} actualitzada${n === 1 ? "" : "es"} en ${r.data.groups_merged} grup${r.data.groups_merged === 1 ? "" : "s"}`
      );
      await loadRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setDeduping(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (onlyLearned && !r.auto_learned) return false;
      if (!q) return true;
      return (r.supplier_name || "").toLowerCase().includes(q) ||
             (r.supplier_nif || "").toLowerCase().includes(q);
    });
  }, [rows, search, onlyLearned]);

  const stats = useMemo(() => ({
    total: rows.length,
    learned: rows.filter((r) => r.auto_learned).length,
    invoices: rows.reduce((s, r) => s + (r.count || 0), 0),
  }), [rows]);

  if (loading) {
    return (
      <div className="card-surface p-12 text-center text-slate-400">
        <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />
        {lang === "es" ? "Cargando proveedores..." : "Carregant proveïdors..."}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="suppliers-dashboard">
      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <KpiCard
          icon={Users}
          label={lang === "es" ? "Proveedores totales" : "Proveïdors totals"}
          value={stats.total}
          accent="blue"
          testid="kpi-total"
        />
        <KpiCard
          icon={Brain}
          label={lang === "es" ? "Auto-aprendidos" : "Auto-après"}
          value={stats.learned}
          accent="emerald"
          subtitle={
            lang === "es"
              ? "≥ 3 facturas con misma clasificación"
              : "≥ 3 factures amb la mateixa classificació"
          }
          testid="kpi-learned"
        />
        <KpiCard
          icon={TrendingUp}
          label={lang === "es" ? "Facturas registradas" : "Factures registrades"}
          value={stats.invoices}
          accent="indigo"
          testid="kpi-invoices"
        />
      </div>

      {/* Filter bar */}
      <div className="card-surface p-3 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={lang === "es"
              ? "Buscar por nombre o NIF..."
              : "Cerca per nom o NIF..."}
            className="bg-white/5 border-white/10 pl-9 text-sm"
            data-testid="suppliers-search"
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
          <input
            type="checkbox"
            checked={onlyLearned}
            onChange={(e) => setOnlyLearned(e.target.checked)}
            className="w-4 h-4 rounded border-white/20 bg-white/5 accent-emerald-500"
            data-testid="suppliers-only-learned"
          />
          <Brain size={12} className="text-emerald-400" />
          {lang === "es" ? "Solo auto-aprendidos" : "Només auto-après"}
        </label>
        {variantGroups.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={dedupe}
            disabled={deduping}
            data-testid="suppliers-dedupe-btn"
            className="h-8 border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20"
          >
            {deduping
              ? <Loader2 size={12} className="mr-1.5 animate-spin" />
              : <Merge size={12} className="mr-1.5" />}
            {lang === "es"
              ? `Fusionar ${variantGroups.length} variante${variantGroups.length === 1 ? "" : "s"}`
              : `Fusionar ${variantGroups.length} variant${variantGroups.length === 1 ? "" : "s"}`}
          </Button>
        )}
        <div className="ml-auto text-[11px] text-slate-500">
          {filtered.length} {lang === "es" ? "resultados" : "resultats"}
        </div>
      </div>

      {/* Table */}
      <div className="card-surface overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <Users className="w-10 h-10 mx-auto mb-3 text-slate-600" />
            <p className="text-sm">
              {rows.length === 0
                ? (lang === "es"
                    ? "Sin proveedores aún — sube tu primera factura"
                    : "Sense proveïdors encara — puja la teva primera factura")
                : (lang === "es"
                    ? "Ningún proveedor coincide con el filtro"
                    : "Cap proveïdor no coincideix amb el filtre")}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.02] border-b border-white/5">
                <tr className="text-xs uppercase tracking-wider text-slate-400">
                  <th className="text-left p-3">
                    {lang === "es" ? "Proveedor" : "Proveïdor"}
                  </th>
                  <th className="text-left p-3 hidden sm:table-cell">NIF/CIF</th>
                  <th className="text-center p-3">
                    {lang === "es" ? "Facturas" : "Factures"}
                  </th>
                  <th className="text-left p-3">
                    {lang === "es" ? "Cuenta PGC dominante" : "Compte PGC dominant"}
                  </th>
                  <th className="text-center p-3 hidden md:table-cell">
                    {lang === "es" ? "Consistencia" : "Consistència"}
                  </th>
                  <th className="text-center p-3">
                    {lang === "es" ? "Estado" : "Estat"}
                  </th>
                  <th className="text-right p-3 hidden lg:table-cell">
                    {lang === "es" ? "Última factura" : "Última factura"}
                  </th>
                  <th className="text-center p-3">
                    <span className="sr-only">
                      {lang === "es" ? "Acciones" : "Accions"}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, idx) => (
                  <SupplierRow key={`${r.supplier_nif || r.supplier_name}-${idx}`}
                               row={r} lang={lang} idx={idx}
                               onDrillDown={onDrillDown} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Help footer */}
      <div className="card-surface p-3 flex items-start gap-3 bg-blue-500/5 border-blue-500/20">
        <Sparkles size={16} className="text-blue-300 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-slate-300 leading-relaxed">
          <strong className="text-white">
            {lang === "es" ? "Cómo funciona el auto-aprendizaje:" : "Com funciona l'auto-aprenentatge:"}
          </strong>{" "}
          {lang === "es"
            ? "Cuando subes una factura del mismo proveedor (NIF idéntico o nombre exacto) por 3ª vez con la misma cuenta PGC, el sistema marca este proveedor como 'Auto-aprendido' y aplica esa clasificación automáticamente a futuras facturas. Cuanto más consistente sea el histórico (5+ idénticas), mayor será la confianza de la IA."
            : "Quan puges una factura del mateix proveïdor (NIF idèntic o nom exacte) per 3a vegada amb el mateix compte PGC, el sistema marca aquest proveïdor com 'Auto-après' i aplica aquesta classificació automàticament a futures factures. Com més consistent sigui l'històric (5+ idèntiques), més alta serà la confiança de la IA."}
        </div>
      </div>
    </div>
  );
}

function SupplierRow({ row, lang, idx, onDrillDown }) {
  const pgcLabel = PGC_LABELS[row.dominant_pgc] || "—";
  return (
    <tr className="border-b border-white/5 hover:bg-white/[0.02]"
        data-testid={`supplier-row-${idx}`}>
      <td className="p-3">
        <div className="text-slate-200 font-medium truncate max-w-[220px]">
          {row.supplier_name || "—"}
        </div>
        <div className="text-[10px] text-slate-500 sm:hidden font-mono">
          {row.supplier_nif || "—"}
        </div>
      </td>
      <td className="p-3 hidden sm:table-cell">
        <span className="text-xs text-slate-400 font-mono">
          {row.supplier_nif || "—"}
        </span>
      </td>
      <td className="p-3 text-center">
        <span className="px-2 py-0.5 rounded text-xs font-mono bg-white/5 border border-white/10 text-slate-200">
          {row.count}
        </span>
      </td>
      <td className="p-3">
        {row.dominant_pgc ? (
          <div>
            <span className="font-mono text-xs text-indigo-300">
              {row.dominant_pgc}
            </span>
            <span className="text-xs text-slate-400 ml-2">
              {pgcLabel}
            </span>
            {row.dominant_category && (
              <div className="text-[10px] text-slate-500 mt-0.5">
                {row.dominant_category}
              </div>
            )}
          </div>
        ) : (
          <span className="text-xs text-slate-500 italic">
            {lang === "es" ? "Sin clasificar" : "Sense classificar"}
          </span>
        )}
      </td>
      <td className="p-3 text-center hidden md:table-cell">
        <ConsistencyBar
          dominant={row.dominant_count}
          classified={row.classified_count}
          ratio={row.ratio}
        />
      </td>
      <td className="p-3 text-center">
        {row.auto_learned ? (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-emerald-500/10 border-emerald-500/40 text-emerald-200 uppercase tracking-wider"
            data-testid={`supplier-learned-${idx}`}
          >
            <CheckCircle2 size={10} />
            {lang === "es" ? "Aprendido" : "Après"}
          </span>
        ) : row.dominant_count >= 1 ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border bg-amber-500/10 border-amber-500/30 text-amber-200 uppercase tracking-wider">
            <AlertCircle size={10} />
            {lang === "es" ? "Aprendiendo" : "Aprenent"}
          </span>
        ) : (
          <span className="text-[10px] text-slate-500 uppercase tracking-wider">
            —
          </span>
        )}
      </td>
      <td className="p-3 text-right hidden lg:table-cell">
        <span className="text-xs text-slate-400 font-mono">
          {row.last_invoice_date || "—"}
        </span>
      </td>
      <td className="p-3 text-center">
        <button
          type="button"
          onClick={() => onDrillDown?.(row)}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-blue-200 hover:text-white hover:bg-blue-500/20 border border-transparent hover:border-blue-500/30 transition-colors"
          title={lang === "es" ? "Ver facturas" : "Veure factures"}
          data-testid={`supplier-drilldown-${idx}`}
        >
          {lang === "es" ? "Ver" : "Veure"}
          <ArrowRight size={11} />
        </button>
      </td>
    </tr>
  );
}

function ConsistencyBar({ dominant, classified, ratio }) {
  if (!classified) return <span className="text-xs text-slate-500">—</span>;
  const pct = Math.round((ratio || 0) * 100);
  const color = pct >= 75 ? "bg-emerald-400"
              : pct >= 50 ? "bg-amber-400"
              : "bg-rose-400";
  return (
    <div className="flex items-center gap-2 justify-center">
      <div className="w-20 h-1.5 bg-white/10 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`}
             style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[10px] text-slate-400 font-mono w-10 text-left">
        {dominant}/{classified}
      </span>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, accent = "blue", subtitle, testid }) {
  const palette = {
    blue: "from-blue-500/10 to-blue-600/5 border-blue-500/20 text-blue-300",
    emerald: "from-emerald-500/10 to-emerald-600/5 border-emerald-500/25 text-emerald-300",
    indigo: "from-indigo-500/10 to-indigo-600/5 border-indigo-500/20 text-indigo-300",
  }[accent];
  return (
    <div className={`card-surface bg-gradient-to-br ${palette} border p-4 relative overflow-hidden`}
         data-testid={testid}>
      <Icon className="absolute -right-3 -top-3 w-14 h-14 opacity-10" />
      <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">
        {label}
      </div>
      <div className="text-2xl font-bold text-white tabular-nums">{value}</div>
      {subtitle && (
        <div className="text-[10px] text-slate-500 mt-1.5 leading-snug">
          {subtitle}
        </div>
      )}
    </div>
  );
}
